import { groupBy, maxBy } from "lodash";
import { ClientId, featureEnabled } from "../../../client-features";
import { SafeReportResponse } from "../Conversion";
import { formatAbbreviatedMoney } from "../Money";
import { findAddressesInRadius, parseCoverage } from "../map/util";
import { addressesToPlottable } from "../map/MapModal";
import { breakDownAddressFieldByTiv, capitalize } from "./util";

export const generateInitialReferralValueBluesky = (
  specialtyPropertyInfo: SafeReportResponse,
  clientId: ClientId
) => {
  const report = specialtyPropertyInfo.report_json;
  const companyName = report.company_info.company_name;

  const addresses = specialtyPropertyInfo.report_json.sovs
    .filter((sov) => sov.is_enabled)
    .flatMap((sov) => sov.addresses);
  const totalTIV = addresses.reduce((total, address) => total + address.tiv, 0);
  const locationsOverTenMil = addresses.filter(
    (address) => address.tiv > 10_000_000
  ).length;

  const locationCount = featureEnabled(
    clientId,
    "location-count-not-include-unique-buildings"
  )
    ? Object.keys(groupBy(addresses, "premises_number")).length
    : addresses.length;
  const buildingOrLocation = featureEnabled(
    clientId,
    "location-count-not-include-unique-buildings"
  )
    ? "building"
    : "location";

  const addressesWithSquareFootage = addresses.filter(
    (address) => address.square_footage && address.square_footage !== ""
  );
  const squareFootageTiv = addressesWithSquareFootage.reduce(
    (total, address) => total + address.tiv,
    0
  );
  const totalSquareFootage = addressesWithSquareFootage.reduce(
    (total, address) =>
      total + Number.parseFloat(address.square_footage ?? "0"),
    0
  );
  const costPerFoot = squareFootageTiv / totalSquareFootage;

  const squareFootageString =
    totalSquareFootage > 0
      ? `<div>The TIV of buildings with square footage provided is ${formatAbbreviatedMoney(
          squareFootageTiv
        )} on ${formatAbbreviatedMoney(
          totalSquareFootage,
          false
        )} square footage for a cost of <b>${formatAbbreviatedMoney(
          costPerFoot
        )}</b> per square foot.</div>
        <br>
        <div><i>Comment on ITV - ideally within 80-120%; if different, comment on any steps taken to address (coinsurance, ACV or RC, pricing considerations, etc.)</i></div>`
      : "";

  const riskClusters = findAddressesInRadius(
    addressesToPlottable(
      addresses,
      specialtyPropertyInfo.report_json.company_info.named_insured ??
        specialtyPropertyInfo.report_json.company_info.company_name
    ).plottables,
    { first: 10 }
  );
  const maxRiskCluster = maxBy(riskClusters, "tiv");
  const windzoneAndAggregationString = `${
    maxRiskCluster
      ? `<div>The greatest TIV aggregation is <b>${formatAbbreviatedMoney(
          maxRiskCluster.tiv
        )}</b> of TIV within a 10 mile radius spanning ${
          maxRiskCluster.included.length
        } ${buildingOrLocation}${
          maxRiskCluster.included.length === 1 ? "" : "s"
        }.</div>
        <br>
        <div><i>Add screenshot of accumulation mapping.</i></div>
        <br>
        <div><i>If there is over $10M of accumulation, comment on the governing peril (i.e. fire amount subject, tornado, wildfire, hail) and if that changes our view of the true stacking exposure.</i></div>
        <br>`
      : ""
  }`;

  const totalBuilding = addresses.reduce(
    (total, address) => total + parseCoverage(address.building_value),
    0
  );
  const totalProperty = addresses.reduce(
    (total, address) => total + parseCoverage(address.bpp_value),
    0
  );
  const totalBi = addresses.reduce(
    (total, address) =>
      total + parseCoverage(address.business_interruption_value),
    0
  );
  const totalTenants = addresses.reduce(
    (total, address) => total + parseCoverage(address.tenant_betterments_value),
    0
  );
  const totalSigns = addresses.reduce(
    (total, address) => total + parseCoverage(address.signs_value),
    0
  );
  const totalGlass = addresses.reduce(
    (total, address) => total + parseCoverage(address.glass_value),
    0
  );

  const coverageTable = `
    <h3>Coverage Breakdown</h3>
    <table>
    <thead>
      <tr>
        <th>Coverages</th>
        <th>Total Limit of Insurance</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Building</td>
        <td>${formatAbbreviatedMoney(totalBuilding)}</td>
      </tr>
      <tr>
        <td>Business Personal Property</td>
        <td>${formatAbbreviatedMoney(totalProperty)}</td>
      </tr>
      <tr>
        <td>Tenant Improvements and Betterments</td>
        <td>${formatAbbreviatedMoney(totalTenants)}</td>
      </tr>
      <tr>
        <td>Business Income</td>
        <td>${formatAbbreviatedMoney(totalBi)}</td>
      </tr>
      <tr>
        <td>Signs</td>
        <td>${formatAbbreviatedMoney(totalSigns)}</td>
      </tr>
      <tr>
        <td>Glass</td>
        <td>${formatAbbreviatedMoney(totalGlass)}</td>
      </tr>
      <tr>
        <td><b>Scheduled TIV</b></td>
        <td><b>${formatAbbreviatedMoney(totalTIV)}</b></td>
      </tr>
    </tbody>
  </table>`;

  const exposureBreakdown =
    addresses.length === 0
      ? ""
      : addresses.length === 1
      ? `<br>
      <div>
      Address: ${addresses[0].street_address}, ${addresses[0].city}, ${
          addresses[0].state
        }, ${addresses[0].zip}
      </div>
            <div>
      ${addresses[0].fire_alarm_type} CST Fire Alarm
      </div>
      <div>
      ${addresses[0].burglar_alarm_type} CST Burglar Alarm
      </div>
      <div>
      ${addresses[0].cooking_class}
      </div>
      <div>
      Built in ${addresses[0].year_built ?? "Unknown"}
      </div>
      ${
        addresses[0].number_of_stories
          ? `<div>${addresses[0].number_of_stories} stories</div>`
          : ""
      }
      <div>
      ${addresses[0].construction_description ?? "Unknown"} Construction
      </div>
      `
      : `<br>
      <div>
      <b>${addresses.length} ${capitalize(
          buildingOrLocation
        )}s Breakdown by TIV</b>
      </div>
      <div>
      States: ${breakDownAddressFieldByTiv("state", addresses, totalTIV)}
      </div>
      <div>
      CST Fire Alarm: ${breakDownAddressFieldByTiv(
        "fire_alarm_type",
        addresses,
        totalTIV
      )}
      </div>
      <div>
      CST Burglar Alarm: ${breakDownAddressFieldByTiv(
        "burglar_alarm_type",
        addresses,
        totalTIV
      )}
      </div>
      <div>
      Year Built: ${breakDownAddressFieldByTiv(
        "year_built",
        addresses,
        totalTIV
      )}
      </div>
      <div>
      Construction: ${breakDownAddressFieldByTiv(
        "construction_description",
        addresses,
        totalTIV
      )}
      </div>
      <div>
      Num Stories: ${breakDownAddressFieldByTiv(
        "number_of_stories",
        addresses,
        totalTIV
      )}
      </div>`;

  return `
  <div>
  <div>
  Hello,
  <br>
  <br>
  </div>
  <div>
  This is a referral for  <b>${companyName}</b> with an effective date of <b>${
    report.company_info.effective_date
  }</b>. There ${
    locationCount === 1 ? "is 1 location" : `are ${locationCount} locations`
  } with a total TIV of <b>${formatAbbreviatedMoney(
    totalTIV
  )}</b>. ${locationsOverTenMil} ${buildingOrLocation}${
    locationsOverTenMil === 1 ? "" : "s"
  } have a TIV over 10M.
  </div>
  <h3>Referral Triggers</h3>
  <div><i>What is causing you to write this referral?</i></div>
  <h3>Proposing</h3>
  <div>Rate: </div>
  <div>Premium: </div>
  <div>AOP: </div>
  <div>Water Damage: </div>
  <div>Wind/Hail: </div>
  <br>
  <div><i>Please add pricing/terms rationale, i.e. why did we land at this rate or premium with these terms?</i></div>
  <br>
  <div><i>Comment on overall risk quality, deductibles chosen, any special endorsements or terms you’re adding, such as ACV, full roof exclusion, BASIC form, vandalism exclusions, etc.</i></div>
  <br>
  <div><i>If hail is a concern, comment on the condition of the roof, calculate roof loss estimate (roof square footage x $20/sq ft) and compare that to what your proposed % deductible equates to based on the reported building values.</i></div>
  <h3>Exposure Overview</h3>
  ${windzoneAndAggregationString}
  ${squareFootageString}
  ${exposureBreakdown}
  <br>
  <div><i>Add photos of the roof satellite imagery as well as street view.</i></div>
  <h3>Hazard Hub</h3>
  <div>Wildfire:</div>
  <div>Wind:</div>
  <div>Hail:</div>
  <div>Crime:</div>
  <h3>Broker</h3>
  <div>
  The broker on this policy is <b>${
    specialtyPropertyInfo.report_json.company_info.broker_agent_name ??
    "unknown"
  }</b> at <b>${
    specialtyPropertyInfo.report_json.company_info.broker_name ?? "unknown"
  }</b>.
  </div>
  <br>
  <div><i>Comment if this is a big relationship for us or one that we are looking to grow.</i></div>
  ${coverageTable}
  <h3>Loss History</h3>
  <div><i>Are there any notable losses to mention?</i></div>
  <br>
  <div>
  Looking forward to your feedback.
  </div>
  </div>
`;
};
